import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Repeat } from "lucide-react"
import type { Appointment } from "../../../types"
import type { CalendarRoutine } from "../../../api/appointments"
import { routinesOnDay } from "../../../utils/routineSchedule"

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
const START_HOUR = 6
const END_HOUR = 22
const HOUR_HEIGHT = 56 // px per hour
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day // Monday start
  d.setDate(d.getDate() + diff)
  return Array.from({ length: 7 }, (_, i) => {
    const r = new Date(d)
    r.setDate(d.getDate() + i)
    return r
  })
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatTime(iso: string) {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", hour12: false })
}

// Vertical offset (px) for a given datetime within the grid.
function yFor(iso: string): number {
  const d = new Date(iso)
  const minutes = (d.getHours() - START_HOUR) * 60 + d.getMinutes()
  return (minutes / 60) * HOUR_HEIGHT
}

function eventColor(a: Appointment) {
  if (a.status === "cancelled")
    return {
      border: "border-red-400/50",
      bg: "bg-red-400/20",
      text: "text-red-200",
      bar: "bg-red-400",
    }
  if (a.status === "completed")
    return {
      border: "border-emerald-400/50",
      bg: "bg-emerald-400/20",
      text: "text-emerald-100",
      bar: "bg-emerald-400",
    }
  if (a.appointment_type === "group_class")
    return {
      border: "border-amber-400/50",
      bg: "bg-amber-400/20",
      text: "text-amber-100",
      bar: "bg-amber-400",
    }
  return {
    border: "border-primary/50",
    bg: "bg-primary/20",
    text: "text-primary",
    bar: "bg-primary",
  }
}

type Positioned = {
  appt: Appointment
  top: number
  height: number
  leftPct: number
  widthPct: number
}

// Side-by-side layout for overlapping appointments (Google-style lane packing).
function layoutDay(appts: Appointment[]): Positioned[] {
  const sorted = [...appts]
    .filter((a) => !isNaN(new Date(a.start_time).getTime()))
    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())

  const result: Positioned[] = []
  let cluster: Appointment[] = []
  let clusterEnd = 0

  function flush() {
    if (cluster.length === 0) return
    const laneEnds: number[] = []
    const laneOf = new Map<number, number>()
    cluster.forEach((a) => {
      const s = new Date(a.start_time).getTime()
      const e = new Date(a.end_time).getTime()
      let lane = laneEnds.findIndex((end) => end <= s)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(e)
      } else {
        laneEnds[lane] = e
      }
      laneOf.set(a.id, lane)
    })
    const cols = laneEnds.length
    cluster.forEach((a) => {
      const lane = laneOf.get(a.id) ?? 0
      const top = yFor(a.start_time)
      result.push({
        appt: a,
        top,
        height: Math.max(20, yFor(a.end_time) - top),
        leftPct: (lane / cols) * 100,
        widthPct: 100 / cols,
      })
    })
    cluster = []
    clusterEnd = 0
  }

  sorted.forEach((a) => {
    const s = new Date(a.start_time).getTime()
    const e = new Date(a.end_time).getTime()
    if (cluster.length > 0 && s >= clusterEnd) flush()
    cluster.push(a)
    clusterEnd = Math.max(clusterEnd, e)
  })
  flush()
  return result
}

export default function WeekView({
  currentDate,
  appointments,
  routines = [],
  onSelectAppointment,
  onDropAppointment,
}: {
  currentDate: Date
  appointments: Appointment[]
  routines?: CalendarRoutine[]
  onSelectAppointment: (a: Appointment) => void
  onDropAppointment?: (appointmentId: number, targetDate: Date, hour: number) => void
}) {
  const weekDays = getWeekDays(currentDate)
  const now = new Date()
  const gridHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT
  const [dragOverDay, setDragOverDay] = useState<number | null>(null)

  const nowOffset =
    now.getHours() >= START_HOUR && now.getHours() < END_HOUR
      ? (((now.getHours() - START_HOUR) * 60 + now.getMinutes()) / 60) * HOUR_HEIGHT
      : null

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  function handleDrop(e: React.DragEvent, day: Date) {
    e.preventDefault()
    setDragOverDay(null)
    const idStr = e.dataTransfer.getData("application/appointment-id")
    if (!idStr || !onDropAppointment) return
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const hour = Math.min(
      END_HOUR - 1,
      Math.max(START_HOUR, START_HOUR + Math.floor(y / HOUR_HEIGHT)),
    )
    onDropAppointment(Number(idStr), day, hour)
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={weekDays[0].toISOString()}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        transition={{ duration: 0.2 }}
        className="max-h-[640px] overflow-auto rounded-xl border border-white/10"
      >
        <div className="min-w-[700px]">
          {/* Header row (sticky) */}
          <div className="sticky top-0 z-20 grid grid-cols-[56px_repeat(7,1fr)] border-b border-white/10 bg-[#0d0f14]">
            <div className="border-r border-white/10" />
            {weekDays.map((day, i) => {
              const isToday = isSameDay(day, now)
              return (
                <div
                  key={i}
                  className={`border-r border-white/5 py-2 text-center ${isToday ? "bg-primary/5" : ""}`}
                >
                  <p className="text-[11px] text-muted">{DAY_LABELS[i]}</p>
                  <p
                    className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                      isToday ? "bg-primary text-black" : "text-white"
                    }`}
                  >
                    {day.getDate()}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Body: time gutter + day columns */}
          <div className="grid grid-cols-[56px_repeat(7,1fr)]">
            {/* Time gutter */}
            <div className="relative border-r border-white/10" style={{ height: gridHeight }}>
              {HOURS.map((h, idx) => (
                <div
                  key={h}
                  className="absolute right-1.5 -translate-y-1/2 text-[10px] text-muted"
                  style={{ top: idx * HOUR_HEIGHT }}
                >
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDays.map((day, i) => {
              const isToday = isSameDay(day, now)
              const isDragOver = dragOverDay === i
              const dayAppts = appointments.filter((a) => isSameDay(new Date(a.start_time), day))
              const positioned = layoutDay(dayAppts)
              const dayRoutines = routinesOnDay(routines, day)

              return (
                <div
                  key={i}
                  onDragOver={handleDragOver}
                  onDragEnter={() => setDragOverDay(i)}
                  onDragLeave={() => setDragOverDay((prev) => (prev === i ? null : prev))}
                  onDrop={(e) => handleDrop(e, day)}
                  className={`relative border-r border-white/5 ${
                    isDragOver ? "bg-primary/10" : isToday ? "bg-primary/[0.03]" : ""
                  }`}
                  style={{ height: gridHeight }}
                >
                  {/* Hour gridlines */}
                  {HOURS.map((h, idx) => (
                    <div
                      key={h}
                      className="absolute inset-x-0 border-t border-white/5"
                      style={{ top: idx * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                    />
                  ))}

                  {/* Routine background blocks (gym slots) */}
                  {dayRoutines.map((r) => {
                    const top = (r.start_hour - START_HOUR) * HOUR_HEIGHT
                    const height = Math.max(20, (r.end_hour - r.start_hour) * HOUR_HEIGHT)
                    if (r.end_hour <= START_HOUR || r.start_hour >= END_HOUR) return null
                    return (
                      <div
                        key={`r-${r.id}`}
                        className="absolute left-0.5 right-0.5 rounded-md border border-violet-400/30 bg-violet-400/10 px-1 py-0.5"
                        style={{ top, height }}
                        title={`Rutina · ${r.user?.name ?? ""}`}
                      >
                        <p className="truncate text-[9px] font-medium text-violet-300">
                          {r.user?.name?.split(" ")[0] ?? "Rutina"}
                        </p>
                      </div>
                    )
                  })}

                  {/* Now indicator */}
                  {isToday && nowOffset !== null && (
                    <div className="absolute inset-x-0 z-10" style={{ top: nowOffset }}>
                      <div className="h-px bg-red-500" />
                      <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                    </div>
                  )}

                  {/* Appointment blocks */}
                  {positioned.map(({ appt, top, height, leftPct, widthPct }) => {
                    const c = eventColor(appt)
                    return (
                      <button
                        key={appt.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("application/appointment-id", String(appt.id))
                          e.dataTransfer.effectAllowed = "move"
                        }}
                        onClick={() => onSelectAppointment(appt)}
                        className={`absolute z-10 overflow-hidden rounded-md border ${c.border} ${c.bg} px-1.5 py-1 text-left transition hover:z-20 hover:shadow-lg cursor-grab active:cursor-grabbing`}
                        style={{
                          top: top + 1,
                          height: height - 2,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                        }}
                      >
                        <div className="flex items-center gap-1">
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.bar}`} />
                          <p className={`truncate text-[10px] font-semibold ${c.text}`}>
                            {appt.participants.map((p) => p.name.split(" ")[0]).join(", ") ||
                              appt.title}
                          </p>
                          {appt.series_id && <Repeat size={9} className="shrink-0 text-muted" />}
                        </div>
                        {height > 34 && (
                          <p className="mt-0.5 truncate text-[9px] text-muted">
                            {formatTime(appt.start_time)}–{formatTime(appt.end_time)}
                          </p>
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
